import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { SeoHead } from "@/components/common/SeoHead";
import { WhatsAppCTA } from "@/components/common/WhatsAppCTA";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Check, Plane, Users, Calendar, Tag } from "lucide-react";

const BREADCRUMB = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://tripsyfy.com" },
    { "@type": "ListItem", position: 2, name: "International Flights", item: "https://tripsyfy.com/international-flight-booking" },
    { "@type": "ListItem", position: 3, name: "Flights to India from USA", item: "https://tripsyfy.com/flights-to-india-from-usa" },
  ],
});

const FAQ_SCHEMA = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Which US cities does Tripsyfy book flights to India from?", acceptedAnswer: { "@type": "Answer", text: "We help travelers book flights to India from all major US cities including New York (JFK, EWR), Chicago (ORD), Los Angeles (LAX), Houston (IAH), Dallas (DFW), San Francisco (SFO), Atlanta (ATL), Washington DC (IAD, DCA), Boston (BOS), and Seattle (SEA)." } },
    { "@type": "Question", name: "Which airlines fly direct to India from the USA?", acceptedAnswer: { "@type": "Answer", text: "Air India, United Airlines, Delta, and Air India Express operate non-stop flights from select US cities. Emirates, Qatar Airways, Etihad, Lufthansa, and British Airways offer one-stop connections via their respective hubs. The best airline depends on your departure city, budget, and preferred layover location." } },
    { "@type": "Question", name: "What is the cheapest time to book flights to India from the USA?", acceptedAnswer: { "@type": "Answer", text: "Fares are generally lower during shoulder seasons (March–April and October–November). The peak season is around Indian festivals (Diwali, Holi) and summer (June–August). Booking 3–6 months in advance and avoiding peak travel dates can save significantly." } },
    { "@type": "Question", name: "Can Tripsyfy help with family flights to India, including children?", acceptedAnswer: { "@type": "Answer", text: "Yes. We specialize in family travel to India, including infants, young children, and elderly parents. We assist with seat assignments, meal preferences, connecting flight coordination, and advice on baggage allowances for families." } },
    { "@type": "Question", name: "Does Tripsyfy help with flexible dates for India flights?", acceptedAnswer: { "@type": "Answer", text: "Absolutely. If you can travel within a range of dates, we search across that window to find the best available fares. Shifting your travel by even a few days can save hundreds of dollars on India-bound international flights." } },
    { "@type": "Question", name: "How do I book flights to India from the USA with Tripsyfy?", acceptedAnswer: { "@type": "Answer", text: "Call us at +1 972-666-1122, message us on WhatsApp, or submit a quote request. Share your US departure city, which city in India you're flying to, your travel dates, number of travelers, and any cabin class or airline preferences." } },
  ],
});

const usCities = ["New York (JFK / EWR)", "Chicago (ORD)", "Los Angeles (LAX)", "Houston (IAH)", "Dallas (DFW)", "San Francisco (SFO)", "Atlanta (ATL)", "Washington DC (IAD)", "Boston (BOS)", "Seattle (SEA)"];
const indiaCities = ["Mumbai (BOM)", "Delhi (DEL)", "Bengaluru (BLR)", "Chennai (MAA)", "Hyderabad (HYD)", "Kolkata (CCU)", "Ahmedabad (AMD)", "Kochi (COK)"];

const whoItIsFor = [
  { icon: Users, title: "Indian-origin families", desc: "Traveling home for weddings, festivals, family visits, and emergencies." },
  { icon: Plane, title: "Students studying in the US", desc: "Semester breaks, summer travel, and year-end return trips." },
  { icon: Calendar, title: "Festival season travelers", desc: "Diwali, Holi, and peak-season travel when fares need careful timing." },
  { icon: Tag, title: "Flexible date travelers", desc: "Those who can shift dates to find the best fares across a window." },
];

const whatWeHandle = [
  "Searching flights across all major airlines serving India",
  "Finding optimal routing via Middle East or European hubs",
  "Multi-city itineraries (e.g., New York → Delhi → Mumbai → New York)",
  "Coordinating group flights for family parties of 5–25 travelers",
  "Flexible date range search to minimize fares",
  "Seat assignments for families with infants and children",
  "Baggage allowance guidance per airline",
  "Booking and issuing confirmed e-tickets",
];

const faqs = [
  { q: "Which US cities does Tripsyfy book flights to India from?", a: "We help travelers from all major US cities: New York (JFK/EWR), Chicago, Los Angeles, Houston, Dallas, San Francisco, Atlanta, Washington DC, Boston, and Seattle." },
  { q: "Which airlines fly direct to India from the USA?", a: "Air India and United operate non-stop flights from select cities. Emirates, Qatar, Etihad, Lufthansa, and British Airways offer one-stop connections. The best option depends on your departure city, budget, and layover preference." },
  { q: "What is the cheapest time to book flights to India from the USA?", a: "Shoulder seasons (March–April and October–November) offer lower fares. Book 3–6 months in advance and avoid peak festival season (Diwali, Holi) and summer school holidays for the best rates." },
  { q: "Can Tripsyfy help with family flights to India, including children?", a: "Yes. We specialize in India family travel including infants, children, and elderly parents — with seat coordination, meal preferences, and baggage guidance." },
  { q: "Does Tripsyfy help with flexible dates for India flights?", a: "Absolutely. If you can travel within a range of dates, we search across that window to find the best available fares — shifting by a few days can save hundreds of dollars." },
  { q: "How do I book flights to India from the USA with Tripsyfy?", a: "Call us at +1 972-666-1122, message us on WhatsApp, or submit a quote request. Share your US departure city, India destination, travel dates, and number of travelers." },
];

export default function FlightsToIndiaPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white">
      <Helmet>
        <script type="application/ld+json">{BREADCRUMB}</script>
        <script type="application/ld+json">{FAQ_SCHEMA}</script>
      </Helmet>
      <SeoHead
        title="Flights to India from USA"
        description="Get help booking flights to India from the USA with Tripsyfy. Family travel, flexible dates, multi-city routes, and personalized flight options from any US city."
        canonical="https://tripsyfy.com/flights-to-india-from-usa"
      />
      <Navbar />

      <section className="relative pt-28 pb-14 bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <p className="text-sky-300 text-xs font-semibold tracking-widest uppercase mb-3">India Flight Specialist</p>
          <h1 className="heading-xl text-white mb-3">Flights to India from the USA</h1>
          <p className="text-base text-white/70 max-w-xl leading-relaxed mb-8">
            Tripsyfy specializes in helping US-based travelers book flights to India — including family groups, diaspora travelers, and anyone navigating the India routing options that online tools handle poorly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/contact"><Button className="bg-accent text-white hover:bg-accent/90">Request a Quote</Button></Link>
            <WhatsAppCTA variant="button" />
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-2xl mb-8">
              <p className="overline mb-3">Coverage</p>
              <h2 className="heading-lg text-primary">US cities and Indian destinations we serve</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <AnimatedSection>
              <div className="bg-white rounded-xl shadow-card p-6">
                <p className="text-sm font-bold text-primary mb-4">US Departure Cities</p>
                <div className="grid grid-cols-2 gap-2">
                  {usCities.map((city) => (
                    <div key={city} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Plane className="h-3.5 w-3.5 text-accent shrink-0" /> {city}
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <div className="bg-white rounded-xl shadow-card p-6">
                <p className="text-sm font-bold text-primary mb-4">Indian Destination Cities</p>
                <div className="grid grid-cols-2 gap-2">
                  {indiaCities.map((city) => (
                    <div key={city} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Plane className="h-3.5 w-3.5 text-accent shrink-0" /> {city}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3">+ all other Indian airports via connections</p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-2xl mb-10">
              <p className="overline mb-3">Who This Is For</p>
              <h2 className="heading-lg text-primary">Built for India-bound travelers from the US</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {whoItIsFor.map(({ icon: Icon, title, desc }, i) => (
              <AnimatedSection key={title} delay={i * 0.06}>
                <div className="bg-surface-subtle rounded-xl p-5">
                  <div className="h-10 w-10 rounded-xl bg-accent/10 flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5 text-accent" />
                  </div>
                  <p className="text-sm font-semibold text-primary mb-1">{title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <AnimatedSection>
              <p className="overline mb-3">What We Handle</p>
              <h3 className="heading-md text-primary mb-5">From search to e-ticket</h3>
              <ul className="space-y-3">
                {whatWeHandle.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="h-5 w-5 rounded-full bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-accent" />
                    </div>
                    <span className="text-sm text-foreground leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <div className="bg-surface-subtle rounded-xl p-7">
                <p className="overline mb-3">Book Now</p>
                <h3 className="heading-md text-primary mb-4">Ready to book your India flight?</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                  Tell us your US departure city, India destination, travel dates, and number of travelers. We'll search across all routing options and get back to you with the best available fares.
                </p>
                <WhatsAppCTA variant="banner" className="mb-3" />
                <Link to="/contact">
                  <Button variant="outline" className="w-full mt-2">Submit a Quote Request</Button>
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-2xl mb-10">
              <p className="overline mb-3">How It Works</p>
              <h2 className="heading-lg text-primary">How we book your India flights</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {[
              { num: "01", title: "Tell us your US city and India destination", desc: "Share your departure airport, India destination city, travel dates, and number of travelers. Flexible dates? Even better." },
              { num: "02", title: "We compare airlines and routings", desc: "We search direct flights and one-stop connections via Middle East and European hubs to find the best combination of price and reliability." },
              { num: "03", title: "You select the right flight", desc: "We present your best options with clear pricing and layover details. You choose — we never pressure or upsell." },
              { num: "04", title: "We book and send your e-ticket", desc: "Once confirmed, we handle the full booking and provide all details including seat assignments and baggage guidance." },
            ].map(({ num, title, desc }, i) => (
              <AnimatedSection key={num} delay={i * 0.08}>
                <div>
                  <p className="text-4xl font-bold text-primary/10 mb-2 leading-none">{num}</p>
                  <h3 className="text-sm font-bold text-primary mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
          <AnimatedSection>
            <p className="overline mb-3 text-center">Common Questions</p>
            <h2 className="heading-lg text-primary text-center mb-10">Flights to India from USA</h2>
          </AnimatedSection>
          <div className="space-y-3">
            {faqs.map(({ q, a }, i) => (
              <AnimatedSection key={i} delay={i * 0.04}>
                <details className="bg-white rounded-xl shadow-card overflow-hidden">
                  <summary className="px-6 py-4 cursor-pointer font-semibold text-primary text-sm list-none flex items-center justify-between gap-4 hover:text-accent transition-colors">
                    {q}<span className="text-accent text-lg leading-none shrink-0">+</span>
                  </summary>
                  <div className="px-6 pb-5">
                    <p className="text-sm text-muted-foreground leading-relaxed">{a}</p>
                  </div>
                </details>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-center">
            <div className="lg:col-span-2">
              <AnimatedSection>
                <h2 className="heading-lg text-white mb-3">Book your India flight today</h2>
                <p className="text-white/70 text-base leading-relaxed">Call, WhatsApp, or submit a quote request — we respond within 24 hours.</p>
              </AnimatedSection>
            </div>
            <div className="flex flex-col gap-3 lg:items-end">
              <Link to="/contact"><Button className="bg-accent text-white hover:bg-accent/90 w-full lg:w-auto">Request a Quote</Button></Link>
              <WhatsAppCTA variant="button" />
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 flex flex-wrap gap-6 text-sm text-white/60">
            <Link to="/international-flight-booking" className="hover:text-white transition-colors">→ International Flight Booking</Link>
            <Link to="/flights-to-africa-from-usa" className="hover:text-white transition-colors">→ Flights to Africa from USA</Link>
            <Link to="/group-travel-planning" className="hover:text-white transition-colors">→ Group Travel Planning</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
